import { AccessibilityInfo, Platform } from "react-native";

const LIVE_REGION_ID = "shared-a11y-live-region";

/**
 * Ensures the shared visually-hidden polite live region exists in the DOM and
 * returns it. Web-only: returns null on native, during SSR, or before
 * `document.body` is available.
 *
 * The region is created **eagerly** (see the module-level call below), well
 * before the first announcement. This is required for VoiceOver + Safari: it
 * only treats an element as a live region if that element was already present
 * when it built its accessibility tree. A region that is created and populated
 * in the same frame is never registered as live, so later text updates are
 * announced by nothing — even on repeated announcements to the same element.
 */
export function ensureLiveRegion(): HTMLElement | null {
  if (Platform.OS !== "web" || typeof document === "undefined" || !document.body) {
    return null;
  }

  let el = document.getElementById(LIVE_REGION_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = LIVE_REGION_ID;
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-atomic", "true");
    el.style.position = "absolute";
    el.style.width = "1px";
    el.style.height = "1px";
    el.style.padding = "0";
    el.style.margin = "-1px";
    el.style.overflow = "hidden";
    el.style.clipPath = "inset(50%)";
    el.style.whiteSpace = "nowrap";
    el.style.border = "0";
    document.body.appendChild(el);
  }
  return el;
}

// Create the live region as soon as this module is evaluated (web only) so it
// is registered as a live region before any announcement fires. Falls back to
// DOMContentLoaded when the body is not ready at evaluation time.
if (Platform.OS === "web" && typeof document !== "undefined") {
  if (document.body) {
    ensureLiveRegion();
  } else if (typeof document.addEventListener === "function") {
    document.addEventListener("DOMContentLoaded", () => ensureLiveRegion(), { once: true });
  }
}

/**
 * Announces a short message to assistive technologies, cross-platform.
 *
 * Use for imperative announcements after an action completes and the UI may
 * change immediately (a declarative live region wouldn't have time to fire) —
 * e.g. a form save/delete, or toggling AI-tailored vs. original content.
 *
 * Web: writes into a visually-hidden polite `aria-live` region (created eagerly
 *   by {@link ensureLiveRegion}). This is required because react-native-web's
 *   `AccessibilityInfo.announceForAccessibility` is a no-op.
 * iOS/Android: forwards to `AccessibilityInfo.announceForAccessibility`, which
 *   no-ops automatically when no screen reader is active. React Native's native
 *   announcement API does not distinguish polite from assertive — both map to
 *   the same primitive on native.
 */
export function announcePolite(message: string): void {
  if (Platform.OS === "ios" || Platform.OS === "android") {
    AccessibilityInfo.announceForAccessibility(message);
    return;
  }

  if (Platform.OS !== "web" || typeof document === "undefined") {
    return;
  }

  const el = ensureLiveRegion();
  if (!el) {
    return;
  }

  el.textContent = "";
  requestAnimationFrame(() => {
    el.textContent = message;
  });
}
